/**
 * DemoLauncher — single landing page for every runnable CSK demo SPA.
 *
 * This is the gallery a visitor sees first; the page has to make each demo
 * recognizable in one line and beautiful in one glance. Cards lead with the
 * catalog `blurb` (the one-line memorable summary), expose the longer
 * `summary` as secondary detail, and route to the dev-server port through a
 * single primary action. The card grid is the page — the header is one
 * paragraph and the rest is demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  WORKSPACE_DEMO_CATALOG,
  demoLocalUrl,
  type DemoCatalogWorkspaceEntry,
} from "./catalog-data.js";
import launcherVisuals from "./demo-launcher-visuals.data.json" with { type: "json" };

export interface DemoLauncherProps {
  /**
   * The `demo:*` script of the SPA hosting the launcher, if any. Its card is
   * marked "you are here" rather than linked away.
   */
  readonly currentScript?: string;
}

const COLORS = launcherVisuals.colors;

/**
 * Choose a stable accent color per catalog entry from a curated CSK palette,
 * so the gallery reads as a panel of distinct theorems rather than a uniform
 * grid. Hash on `script` keeps each demo's color fixed across renders.
 */
const ACCENTS: readonly string[] = launcherVisuals.accents;

function accentFor(script: string): string {
  let hash = 0;
  for (let i = 0; i < script.length; i++) {
    hash = (hash * 31 + script.charCodeAt(i)) | 0;
  }
  const index = ((hash % ACCENTS.length) + ACCENTS.length) % ACCENTS.length;
  return ACCENTS[index] ?? COLORS.brandSoft;
}

/**
 * WCAG relative luminance of a `#RRGGBB` color. Used to pick a readable
 * foreground for an accent-backed CTA — bright accents like `#38bdf8` or
 * `#eab308` do not pass against white text, so the button switches to dark
 * ink whenever the accent itself is light.
 */
function relativeLuminance(hex: string): number {
  const channel = (start: number): number => {
    const value = parseInt(hex.slice(start, start + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  };
  const r = channel(1);
  const g = channel(3);
  const b = channel(5);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function readableTextOn(background: string): string {
  return relativeLuminance(background) > 0.45 ? COLORS.ink : "#ffffff";
}

interface LauncherCardProps {
  readonly card: WorkspaceDemoCardModel;
  readonly isCurrent: boolean;
}

interface WorkspaceDemoCardModel {
  readonly entry: DemoCatalogWorkspaceEntry;
  readonly accent: string;
  readonly accentText: string;
}

const LAUNCHER_CARDS: readonly WorkspaceDemoCardModel[] = Object.freeze(
  WORKSPACE_DEMO_CATALOG.map((entry) => {
    const accent = accentFor(entry.script);
    return Object.freeze({ entry, accent, accentText: readableTextOn(accent) });
  }),
);

function DemoRouteSketch({
  entry,
  accent,
}: {
  readonly entry: DemoCatalogWorkspaceEntry;
  readonly accent: string;
}): React.JSX.Element {
  return (
    <div
      aria-label={`${entry.title} launch path`}
      className="relative mt-1 grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-2 rounded-lg border border-[var(--csk-border)] bg-[var(--csk-elevated)] p-2.5"
    >
      {["Hub", `Port ${entry.port}`, "Demo"].map((label, index) => (
        <React.Fragment key={label}>
          {index > 0 ? (
            <span className="h-0.5 w-5 rounded-full bg-[var(--csk-border)]" aria-hidden />
          ) : null}
          <span
            className="grid min-h-12 place-items-center rounded-md border bg-[var(--csk-surface)] px-2 text-center text-[11.5px] font-extrabold leading-tight text-[var(--csk-text)] shadow-sm"
            style={{
              borderColor: index === 1 ? `${accent}66` : "var(--csk-border)",
              color: index === 1 ? accent : undefined,
            }}
          >
            {label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

function LauncherCard({ card, isCurrent }: LauncherCardProps): React.JSX.Element {
  const { entry, accent, accentText } = card;
  return (
    <article
      className="relative flex flex-col gap-2 rounded-lg border border-[var(--csk-border)] bg-[var(--csk-surface)] p-4 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-[var(--csk-accent)] hover:bg-[var(--csk-elevated)] hover:shadow-[0_16px_34px_rgba(42,50,70,0.12)]"
      style={{
        background: isCurrent
          ? `linear-gradient(135deg, ${COLORS.current} 0%, color-mix(in srgb, var(--csk-surface) 82%, transparent) 70%)`
          : "linear-gradient(180deg, color-mix(in srgb, var(--csk-surface) 94%, transparent), color-mix(in srgb, var(--csk-elevated) 88%, transparent))",
        borderLeft: `4px solid ${accent}`,
      }}
    >
      <div className="flex items-baseline gap-2">
        <h2 className="m-0 flex-1 text-base font-extrabold text-[var(--csk-bright)]">
          {entry.title}
        </h2>
        <span
          aria-label={`Port ${String(entry.port)}`}
          className="rounded-md border px-1.5 py-0.5 text-[11px] font-bold"
          style={{
            background: `${accent}18`,
            border: `1px solid ${accent}55`,
            color: accent,
          }}
        >
          :{entry.port}
        </span>
      </div>
      <p className="m-0 text-[13.5px] font-semibold leading-[1.45] text-[var(--csk-text)]">
        {entry.blurb}
      </p>
      <p className="m-0 flex-1 text-[12.5px] leading-normal text-[var(--csk-muted)]">
        {entry.summary}
      </p>
      <DemoRouteSketch entry={entry} accent={accent} />
      <div className="mt-0.5 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[12.5px] font-semibold text-[var(--csk-muted)]">
          Visual workspace
        </span>
        {isCurrent ? (
          <span
            className="inline-flex items-center gap-1.5 text-[12.5px] font-bold"
            style={{
              color: COLORS.brand,
            }}
          >
            <span
              aria-hidden
              style={{
                background: COLORS.brand,
                borderRadius: 999,
                boxShadow: `0 0 0 3px ${COLORS.brandWash}`,
                display: "inline-block",
                height: 8,
                width: 8,
              }}
            />
            You are here
          </span>
        ) : (
          <a
            href={demoLocalUrl(entry)}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12.5px] font-bold no-underline transition hover:-translate-y-px"
            style={{
              background: accent,
              color: accentText,
            }}
          >
            Open demo
            <span aria-hidden style={{ fontWeight: 800 }}>
              →
            </span>
          </a>
        )}
      </div>
    </article>
  );
}

export function DemoLauncher({ currentScript }: DemoLauncherProps): React.JSX.Element {
  return (
    <section className="mx-auto max-w-[72rem] px-5 pb-16 pt-8 text-[var(--csk-text)]">
      <header className="mb-6">
        <div
          className="mb-1.5 text-xs font-extrabold"
          style={{
            color: COLORS.brand,
          }}
        >
          Categorical Safety Kernel — interactive demos
        </div>
        <h1 className="m-0 mb-2 text-[clamp(1.7rem,3vw,2.35rem)] font-black leading-tight text-[var(--csk-bright)]">
          Demo workspace
        </h1>
        <p className="m-0 max-w-[46rem] text-[15.5px] leading-7 text-[var(--csk-muted)]">
          {WORKSPACE_DEMO_CATALOG.length} independently runnable demonstrations of vulnerability
          classes made structurally inexpressible. Each card shows the launch surface and the
          concept it demonstrates before you open the workspace.
        </p>
      </header>

      <div className="grid grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] gap-4">
        {LAUNCHER_CARDS.map((card) => (
          <LauncherCard
            key={card.entry.script}
            card={card}
            isCurrent={card.entry.script === currentScript}
          />
        ))}
      </div>
    </section>
  );
}
